import React, { useMemo, useRef } from "react";
import { Autocomplete, TextField } from "@mui/material";

const pickerSx = {
  width: "100%",
  "&& .MuiOutlinedInput-root": {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    backgroundColor: "var(--od-surface)",
    color: "var(--od-text)",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--od-interactive-boundary)" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--od-primary)" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--od-primary)" },
  },
  "&& .MuiOutlinedInput-root .MuiAutocomplete-input": {
    padding: "6px 12px",
    lineHeight: "20px",
    height: "20px",
    color: "var(--od-text)",
    "&::placeholder": { color: "var(--od-muted)", opacity: 1 },
  },
  "& .MuiInputLabel-root": { color: "var(--od-muted)" },
  "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
    color: "var(--od-muted)",
  },
  "& .MuiAutocomplete-tag": {
    backgroundColor: "var(--od-surface-2)",
    color: "var(--od-text)",
    fontSize: "0.8125rem",
    "& .MuiChip-deleteIcon": {
      color: "var(--od-muted)",
      "&:hover": { color: "var(--od-text)" },
    },
  },
};

export interface OptionItem {
  label: string;
  value: string;
}

interface AutocompleteSelectProps {
  label?: string;
  /** Accessible name when the control is labelled visually elsewhere. */
  ariaLabel?: string;
  /** Input id so an external `<label htmlFor>` can name the combobox. */
  id?: string;
  options: OptionItem[];
  value: string | string[] | null;
  onChange: (value: string | string[] | null) => void;
  multiple?: boolean;
  freeSolo?: boolean;
  size?: "small" | "medium";
  disabled?: boolean;
  placeholder?: string;
  onInputChange?: (text: string) => void;
  debounceMs?: number;
  /** 32px control to match the 8-unit button row in the table footer. */
  compact?: boolean;
  /**
   * Drop the clear (×) button. Worth it where the control must always hold a
   * value and the icon would otherwise eat the text box.
   */
  disableClearable?: boolean;
  commitTypedOnBlur?: boolean;
}

export const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  label,
  ariaLabel,
  id,
  options,
  value,
  onChange,
  multiple = false,
  freeSolo = false,
  size = "small",
  disabled,
  placeholder,
  onInputChange,
  debounceMs = 200,
  compact = false,
  disableClearable = false,
  commitTypedOnBlur = false,
}) => {
  const typedRef = useRef<string | null>(null);
  const optionsKey = options.map((o) => `${o.value}\0${o.label}`).join("\n");
  // Content, not array identity. A parent that rebuilds `options` each render
  // must not mint a new value object and close the open list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableOptions = useMemo(() => options, [optionsKey]);
  const optionLabels = useMemo(
    () => new Map(stableOptions.map((o) => [o.value, o.label])),
    [stableOptions]
  );
  const debounceRef = useRef<number | undefined>(undefined);
  // A fresh object every render makes MUI treat the value as changed and
  // close an open list. Identity must follow the selected value, not the render.
  const selected = useMemo(() => {
    if (multiple) {
      const picked = Array.isArray(value) ? value : [];
      return picked.map((v) => ({ label: optionLabels.get(v) || v, value: v }));
    }
    if (typeof value !== "string" || value.length === 0) return null;
    return { label: optionLabels.get(value) || value, value };
  }, [multiple, optionLabels, value]);

  return (
    <Autocomplete
      disablePortal={!compact}
      sx={[
        pickerSx,
        multiple ? { "&& .MuiOutlinedInput-root": { height: "auto" } } : {},
        compact
          ? {
              "&& .MuiOutlinedInput-root": {
                height: "32px",
                "@media (max-width: 639px), (pointer: coarse)": {
                  height: multiple ? "auto" : "44px",
                  minHeight: 44,
                },
              },
              "&& .MuiOutlinedInput-root .MuiAutocomplete-input": {
                padding: "6px 8px",
                fontSize: "0.75rem",
              },
            }
          : {},
      ]}
      options={stableOptions}
      multiple={multiple}
      freeSolo={freeSolo}
      disableClearable={disableClearable}
      size={size}
      disabled={disabled}
      value={selected}
      isOptionEqualToValue={(opt, val) => opt.value === (val as OptionItem).value}
      onChange={(_, val) => {
        if (multiple) {
          const arr = (val as (string | OptionItem)[]).map(v => typeof v === 'string' ? v : v.value);
          onChange(arr);
        } else {
          typedRef.current = null;
          const v = val as string | OptionItem | null;
          if (v == null) onChange(null);
          else if (typeof v === 'string') {
            const text = v.trim();
            const match = stableOptions.find((o) => o.label === text || o.value === text);
            onChange(text === '' ? null : match ? match.value : text);
          } else onChange(v.value);
        }
      }}
      onInputChange={(_, text, reason) => {
        typedRef.current = reason === "input" ? text : null;
        if (!onInputChange) return;
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => onInputChange(text), debounceMs);
      }}
      onBlur={() => {
        const typed = typedRef.current;
        typedRef.current = null;
        if (!commitTypedOnBlur || multiple || typed === null) return;
        const text = typed.trim();
        const match = stableOptions.find(
          (o) => o.label === text || o.value === text
        );
        const next = text === "" ? null : match ? match.value : text;
        if (next !== value) onChange(next);
      }}
      getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.label)}
      componentsProps={{
        paper: {
          sx: {
            backgroundColor: "var(--od-surface)",
            backgroundImage: "none",
            color: "var(--od-text)",
            border: "1px solid var(--od-border)",
            borderRadius: "8px",
            "& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading": { color: "var(--od-muted)" },
          },
        },
      }}
      ListboxProps={{
        style: { maxHeight: "min(300px, calc(100vh - 96px))", overflowY: "auto" },
        sx: {
          backgroundColor: "var(--od-surface)",
          color: "var(--od-text)",
          fontSize: "0.8125rem",
          "&& .MuiAutocomplete-option": {
            "&:hover, &.Mui-focused, &.Mui-focusVisible": { backgroundColor: "var(--od-surface-2)" },
            "&[aria-selected='true']": {
              backgroundColor: "var(--od-surface-2)",
              "&.Mui-focused, &:hover": { backgroundColor: "var(--od-surface-2)" },
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          id={id ?? params.id}
          label={label}
          placeholder={placeholder}
          size="small"
          inputProps={{
            ...params.inputProps,
            ...(id ? { id } : {}),
            "aria-label": ariaLabel,
          }}
        />
      )}
    />
  );
};

export default AutocompleteSelect;
